type RichTextNode = {
  type?: string;
  text?: string;
  content?: RichTextNode[];
};

type BlogExcerptInput = {
  content?: string | object | null;
  excerpt?: string | null;
};

const DEFAULT_LIMIT = 200;

export function extractExcerptFromContent(content: string | object | null | undefined, limit = DEFAULT_LIMIT): string {
  const text = extractPlainText(content);
  return truncateText(text, limit);
}

export function blogCardExcerpt(post: BlogExcerptInput, limit = DEFAULT_LIMIT): string {
  // Lista ne šalje tijelo, pa kartica čita spremljeni excerpt. Migrirani video
  // postovi ga počinju golim YouTube linkom koji je u tijelu postao embed.
  return extractExcerptFromContent(post.content, limit) || truncateText(stripUrls(post.excerpt ?? ""), limit);
}

function stripUrls(text: string): string {
  return text.replace(/https?:\/\/\S+/g, " ");
}

/**
 * Excerpt for saving a post: text from the body, else the existing excerpt,
 * else the title. Posts made only of embeds, images or headings have no body
 * text, and the excerpt is not an editable field, so it must never come out empty.
 */
export function resolveBlogExcerpt(
  input: BlogExcerptInput & { title?: string | null },
  limit = DEFAULT_LIMIT,
): string {
  return (
    extractExcerptFromContent(input.content, limit) ||
    truncateText(input.excerpt ?? "", limit) ||
    truncateText(input.title ?? "", limit)
  );
}

function extractPlainText(content: string | object | null | undefined): string {
  if (!content) return "";

  try {
    const parsed = typeof content === "string" ? JSON.parse(content) : content;
    const root = parsed as { content?: RichTextNode[] };
    if (!Array.isArray(root.content)) return "";

    const parts: string[] = [];
    for (const node of root.content) {
      if (isPreviewTextNode(node)) {
        collectText(node, parts);
      }
    }
    return parts.join(" ").replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
}

function isPreviewTextNode(node: RichTextNode): boolean {
  return node.type === "paragraph" || node.type === "bulletList" || node.type === "orderedList" || node.type === "blockquote";
}

function collectText(node: RichTextNode, parts: string[]): void {
  if (node.text) parts.push(node.text);
  if (!Array.isArray(node.content)) return;

  for (const child of node.content) {
    collectText(child, parts);
  }
}

function truncateText(text: string, limit: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= limit) return normalized;
  const sliced = normalized.slice(0, limit).trimEnd();
  const lastSpace = sliced.lastIndexOf(" ");
  const preview = lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced;
  return `${preview}...`;
}
